import { useState, useEffect } from "react";
import { useModalStore } from "../../../stores/modalStore.js";

const PERM_DEFS = [
  { key: 'view_channel',       label: 'View Channel',       desc: 'Allow members to see this channel' },
  { key: 'send_messages',      label: 'Send Messages',      desc: 'Allow members to send messages' },
  { key: 'embed_links',        label: 'Embed Links',        desc: 'Allow posting URLs with previews' },
  { key: 'attach_files',       label: 'Attach Files',       desc: 'Allow uploading files' },
  { key: 'read_history',       label: 'Read History',       desc: 'Allow reading past messages' },
  { key: 'mention_everyone',   label: 'Mention @everyone',  desc: 'Allow using @everyone / @here' },
  { key: 'add_reactions',      label: 'Add Reactions',      desc: 'Allow adding emoji reactions' },
  { key: 'manage_messages',    label: 'Manage Messages',    desc: 'Allow deleting and pinning messages' },
  { key: 'manage_channel',     label: 'Manage Channel',     desc: 'Allow editing channel name/topic' },
  { key: 'use_slash_commands', label: 'Use Slash Commands', desc: 'Allow using / commands' },
  { key: 'connect',            label: 'Connect (Voice)',    desc: 'Allow connecting to voice channels' },
  { key: 'speak',              label: 'Speak (Voice)',      desc: 'Allow speaking in voice channels' },
];

const STATES = ['inherit', 'allow', 'deny'];
const STATE_ICONS  = { inherit: '➖', allow: '✅', deny: '🚫' };
const STATE_COLORS = { inherit: 'rgba(255,255,255,0.3)', allow: '#22c55e', deny: '#ef4444' };

export function ChannelPermissionsModal() {
  const { isOpen, type, data, onClose } = useModalStore();
  const serverId  = data?.serverId;
  const channelId = data?.channelId;
  const channelName = data?.channelName || 'channel';

  const [roles,    setRoles]    = useState([]);
  const [selRole,  setSelRole]  = useState(null);
  const [perms,    setPerms]    = useState({});
  const [saving,   setSaving]   = useState(false);
  const [saved,    setSaved]    = useState(false);
  const [loading,  setLoading]  = useState(false);

  useEffect(() => {
    if (!isOpen || type !== 'channelPerms' || !serverId) return;
    // Load roles
    fetch(`/api/servers/${serverId}/roles`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
    })
      .then(r => r.json())
      .then(d => {
        const list = d.roles || [];
        setRoles(list);
        if (list.length) setSelRole(list[0].id);
      })
      .catch(() => {});
  }, [isOpen, type, serverId]);

  useEffect(() => {
    if (!selRole || !channelId || !serverId) return;
    setLoading(true);
    fetch(`/api/servers/${serverId}/channels/${channelId}/permissions/${selRole}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
    })
      .then(r => r.json())
      .then(d => { if (d.success) setPerms(d.permissions || {}); })
      .catch(() => setPerms({}))
      .finally(() => setLoading(false));
  }, [selRole, channelId, serverId]);

  if (!isOpen || type !== 'channelPerms') return null;

  const cycleState = (key) => {
    setPerms(p => {
      const cur = p[key] || 'inherit';
      const next = STATES[(STATES.indexOf(cur) + 1) % STATES.length];
      return { ...p, [key]: next };
    });
  };

  const handleSave = async () => {
    if (!selRole || !channelId || !serverId) return;
    setSaving(true);
    try {
      await fetch(`/api/servers/${serverId}/channels/${channelId}/permissions/${selRole}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
        body: JSON.stringify({ permissions: perms }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (_) {}
    setSaving(false);
  };

  const activeRole = roles.find(r => r.id === selRole);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }} onClick={onClose}>
      <div style={{ width: '100%', maxWidth: 580, maxHeight: '88vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-primary,#13141f)', borderRadius: 16, boxShadow: '0 24px 64px rgba(0,0,0,0.6)', overflow: 'hidden' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#fff' }}>🔒 Channel Permissions</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>#{channelName} — override permissions per role</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 18, cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* Role list sidebar */}
          <div style={{ width: 160, borderRight: '1px solid rgba(255,255,255,0.07)', overflowY: 'auto', padding: '12px 8px', flexShrink: 0 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.06em', padding: '0 8px', marginBottom: 6 }}>Roles</div>
            {roles.map(r => (
              <button key={r.id} onClick={() => setSelRole(r.id)}
                style={{
                  width: '100%', padding: '8px 10px', borderRadius: 8, background: selRole === r.id ? 'rgba(99,102,241,0.2)' : 'none',
                  border: selRole === r.id ? '1px solid rgba(99,102,241,0.4)' : '1px solid transparent',
                  color: selRole === r.id ? '#fff' : 'rgba(255,255,255,0.5)',
                  textAlign: 'left', fontSize: 13, cursor: 'pointer', marginBottom: 3, display: 'flex', alignItems: 'center', gap: 6,
                }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: r.color || '#6b7280', flexShrink: 0, display: 'inline-block' }} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>
              </button>
            ))}
            {roles.length === 0 && (
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', padding: '0 8px', fontStyle: 'italic' }}>No roles</div>
            )}
          </div>

          {/* Permission toggles */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
            {!selRole ? (
              <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 13, marginTop: 40, textAlign: 'center' }}>Select a role</div>
            ) : loading ? (
              <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 13, marginTop: 40, textAlign: 'center' }}>Loading…</div>
            ) : (
              <>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>
                  Overrides for @{activeRole?.name}
                </div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', marginBottom: 14 }}>
                  Click to cycle: ➖ Inherit &nbsp; ✅ Allow &nbsp; 🚫 Deny
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {PERM_DEFS.map(p => {
                    const state = perms[p.key] || 'inherit';
                    return (
                      <div key={p.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.06)' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, color: '#fff', fontWeight: 500 }}>{p.label}</div>
                          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>{p.desc}</div>
                        </div>
                        <button onClick={() => cycleState(p.key)}
                          style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: STATE_COLORS[state], marginLeft: 10, padding: '2px 6px', borderRadius: 6, transition: 'all 0.15s' }}
                          title={state}>
                          {STATE_ICONS[state]}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: '1px solid rgba(255,255,255,0.07)', display: 'flex', justifyContent: 'flex-end', gap: 10, flexShrink: 0 }}>
          <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 8, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)', fontSize: 13, cursor: 'pointer' }}>Close</button>
          <button onClick={handleSave} disabled={saving || !selRole}
            style={{ padding: '8px 20px', borderRadius: 8, background: saved ? '#22c55e' : 'var(--color-primary,#6366f1)', border: 'none', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'background 0.2s', opacity: !selRole ? 0.5 : 1 }}>
            {saved ? '✓ Saved' : saving ? 'Saving…' : 'Save Overrides'}
          </button>
        </div>
      </div>
    </div>
  );
}
